// Galéria-kép kivágása: fókuszpont (focusX/focusY, a kép %-ában), nagyítás (zoom, 100–300 %)
// és az eredeti kép w/h aránya (imageAspect). Ugyanez a számítás fut a kártyán, a részletes
// oldalon, a Quick View-ban és a kivágás-dialógus előnézetében, így mindenhol ugyanazt látja a szolgáltató.
//
// Működés: a kép úgy méreteződik, hogy a keretet kitöltse (cover), majd z-szeresére nagyítjuk.
// A fókuszpont a nagyított képen a keret közepére kerül, amennyire a kép széle engedi.
// A méretet és az eltolást CSS container-query egységekkel (cqw/cqh) számoljuk, így a keret
// bármilyen arányú lehet (kártya 4:3, részletes 16:9, négyzet, függőleges).

export const CROP_MIN_ZOOM = 100;
export const CROP_MAX_ZOOM = 300;

const clampNumber = (value, min, max, fallback) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
};

/** A kép kivágási paramétereinek normalizálása (hiányzó értékekre alapértelmezés). */
export const normalizeCrop = (image) => {
  const aspect = Number(image?.imageAspect);
  return {
    focusX: clampNumber(image?.focusX, 0, 100, 50),
    focusY: clampNumber(image?.focusY, 0, 100, 50),
    zoom: clampNumber(image?.zoom, CROP_MIN_ZOOM, CROP_MAX_ZOOM, 100),
    imageAspect: Number.isFinite(aspect) && aspect > 0 ? aspect : null,
  };
};

/**
 * Stílus a kép <img> elemére. A szülő elemnek (keret) relatívnak és `containerType: 'size'`-nak
 * kell lennie, és kitöltött méretűnek. `container: false` esetén (pl. mobil, auto magasság)
 * egyszerű object-position fallback fut.
 *
 * @returns {object} React style / MUI sx objektum
 */
export const getCropImageStyle = (image, { container = true } = {}) => {
  const c = normalizeCrop(image);
  if (!container || !c.imageAspect) {
    return { objectPosition: `${c.focusX}% ${c.focusY}%` };
  }

  const ir = c.imageAspect;
  const z = c.zoom / 100;
  const tx = c.focusX / 100;
  const ty = c.focusY / 100;
  // A nagyított kép mérete a kereten belül (cover × zoom)
  const W = `max(100cqw, 100cqh * ${ir}) * ${z}`;
  const H = `max(100cqh, 100cqw / ${ir}) * ${z}`;

  return {
    position: 'absolute',
    maxWidth: 'none',
    maxHeight: 'none',
    objectFit: 'fill',
    width: `calc(${W})`,
    height: `calc(${H})`,
    // Fókusz a keret közepére, de a kép széle nem húzható be a keret mögé
    left: `clamp(calc(100cqw - ${W}), calc(50cqw - (${W}) * ${tx}), 0px)`,
    top: `clamp(calc(100cqh - ${H}), calc(50cqh - (${H}) * ${ty}), 0px)`,
  };
};

/**
 * A kivágott terület a kép törtrészeiben (x, y: bal-felső sarok; w, h: méret), egy adott
 * keretarányhoz (frameAspect = szélesség / magasság). Ugyanazt a geometriát számolja, mint a CSS.
 * A kivágás-dialógus ezzel rajzolja ki a fehér keretet a képre.
 */
export const getCropRect = (image, frameAspect) => {
  const c = normalizeCrop(image);
  const ir = c.imageAspect || 1;
  const fa = frameAspect;
  const z = c.zoom / 100;
  // Normalizált egység: a keret magassága 1, szélessége fa
  const W = Math.max(fa, ir) * z;
  const H = Math.max(1, fa / ir) * z;
  const left = Math.min(0, Math.max(fa - W, fa / 2 - W * (c.focusX / 100)));
  const top = Math.min(0, Math.max(1 - H, 0.5 - H * (c.focusY / 100)));
  return {
    x: -left / W,
    y: -top / H,
    w: fa / W,
    h: 1 / H,
  };
};

/** Egy kép kivágása után a mentendő mezők (a dialógus Kész gombja ezt adja vissza). */
export const cropFields = (image) => {
  const c = normalizeCrop(image);
  return { focusX: c.focusX, focusY: c.focusY, zoom: c.zoom, imageAspect: c.imageAspect };
};
